import React, { useState, useEffect, useCallback } from "react";
import { Card, Badge, Avatar, Button } from "components/ui";
import {
  CalendarDaysIcon,
  CalendarIcon,
  ClockIcon,
  UserCircleIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  PlusIcon,
  ArrowPathIcon,
  FlagIcon,
  FireIcon,
  PaperAirplaneIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DevicePhoneMobileIcon,
  EllipsisVerticalIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { hasPermission } from "utils/permissionsUtils";
import { getCommunicationAndTelephonyStatus } from "../../../services/leadsService";
import { formatDate, formatTime, getLocalDateTime } from "../../../utils/dateUtils";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { Fragment } from "react";

// Final statuses that cannot be updated further
const FINAL_STATUS_IDS = [2, 3, 4, 5]; // Completed, Missed, Cancelled, Failed
const FINAL_STATUS_NAMES = ['Completed', 'Missed', 'Cancelled', 'Failed'];

// Helper to check if a follow-up status is final
const isStatusFinal = (followUp) => {
  if (!followUp) return false;
  const statusId = followUp.StatusId || followUp.statusId;
  const statusName = followUp.StatusName || followUp.status;
  return FINAL_STATUS_IDS.includes(statusId) || FINAL_STATUS_NAMES.includes(statusName);
};

// Helper to check if a follow-up is overdue
const isFollowUpOverdue = (followUp) => {
  if (!followUp || !followUp.ScheduledDate) return false;
  const scheduledDate = getLocalDateTime(followUp.ScheduledDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return scheduledDate < today;
};

const StatusCards = ({
  isMobile,
  leadData,
  onAssignOwner,
  onEditFollowUp,
  onUpdateStatus,
  onAddFollowUp,
  // Lead Management handlers
  onChangeStage,
  onSetPriority,
  onSetInteractionLevel,
  isReadOnly,
}) => {
  const isDuplicate = Boolean(
    isReadOnly ||
    leadData?.IsDuplicate ||
    leadData?.isDuplicate ||
    leadData?.ParentLeadId ||
    leadData?.parentLeadId
  );
  const isViewOnly = Boolean(isDuplicate || isReadOnly || leadData?.IsStudent);

  // State for communication and telephony stats
  const [communicationStats, setCommunicationStats] = useState({
    emailSent: 0,
    smsSent: 0,
  });
  const [telephonyStats, setTelephonyStats] = useState({
    inboundCalls: 0,
    outboundCalls: 0,
  });
  const [currentFollowUpIndex, setCurrentFollowUpIndex] = useState(0);

  const leadId = leadData?.id || leadData?.leadId || leadData?.LeadId;

  // Fetch communication and telephony stats
  const fetchStats = useCallback(async () => {
    if (!leadId) {
      return;
    }

    try {
      const response = await getCommunicationAndTelephonyStatus(leadId);
      if (response?.success && response.data) {
        const statusData = response.data;

        setCommunicationStats({
          emailSent:
            statusData.emailSent ??
            statusData.EmailSent ??
            0,
          smsSent:
            statusData.smsSent ??
            statusData.SMSSent ??
            0,
        });

        setTelephonyStats({
          inboundCalls:
            statusData.inboundCalls ??
            statusData.inboundCall ??
            statusData.InBoundCalls ??
            0,
          outboundCalls:
            statusData.outboundCalls ??
            statusData.outboundCall ??
            statusData.OutBoundCalls ??
            0,
        });
      }
    } catch (error) {
      console.error("Error fetching communication stats:", error);
    }
  }, [leadId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  // Helper function to get the next upcoming follow-up
  const getNextFollowUp = () => {
    if (
      !leadData ||
      !leadData.upcomingFollowUps ||
      leadData.upcomingFollowUps.length === 0
    ) {
      return null;
    }

    // Filter pending follow-ups and sort by date
    const pendingFollowUps = leadData.upcomingFollowUps
      .filter(
        (followUp) =>
          followUp.Status === 1 || followUp.StatusName === "Pending",
      )
      .sort((a, b) => getLocalDateTime(a.ScheduledDate) - getLocalDateTime(b.ScheduledDate));

    return pendingFollowUps.length > 0 ? pendingFollowUps[0] : null;
  };

  // Helper function to get all pending follow-ups (for carousel)
  const getPendingFollowUps = () => {
    if (
      !leadData ||
      !leadData.upcomingFollowUps ||
      leadData.upcomingFollowUps.length === 0
    ) {
      return [];
    }

    return leadData.upcomingFollowUps
      .filter(
        (followUp) =>
          followUp.Status === 1 || followUp.StatusName === "Pending",
      )
      .sort((a, b) => getLocalDateTime(a.ScheduledDate) - getLocalDateTime(b.ScheduledDate));
  };

  const allPendingFollowUps = getPendingFollowUps();
  const currentFollowUp = allPendingFollowUps[currentFollowUpIndex] || null;

  // Reset index when follow-ups change
  useEffect(() => {
    if (currentFollowUpIndex >= allPendingFollowUps.length) {
      setCurrentFollowUpIndex(Math.max(0, allPendingFollowUps.length - 1));
    }
  }, [allPendingFollowUps.length, currentFollowUpIndex]);

  // Navigation handlers
  const handlePrevFollowUp = () => {
    setCurrentFollowUpIndex((prev) =>
      prev > 0 ? prev - 1 : allPendingFollowUps.length - 1
    );
  };

  const handleNextFollowUp = () => {
    setCurrentFollowUpIndex((prev) =>
      prev < allPendingFollowUps.length - 1 ? prev + 1 : 0
    );
  };

  const isAssigned = (name) => {
    if (!name) return false;
    const n = name.toString().trim().toLowerCase();
    return n !== "n/a" && n !== "unassigned" && n !== "";
  };

  const leadOwnerName = leadData?.assignedOwner;
  const isOwnerAssigned = isAssigned(leadOwnerName);

  const stageName = leadData?.stage || leadData?.StageName || "N/A";
  const substageName = leadData?.subStage || leadData?.SubStageName || leadData?.substage || "N/A";
  const isChangeStageRestricted = (stageName?.toLowerCase() === "closed" || stageName?.toLowerCase() === "close") && 
                                   (substageName?.toLowerCase() === "win" || substageName?.toLowerCase() === "registered");

  const hasAnyPersonnel = isOwnerAssigned;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Lead Management Section */}
      <div>
        <h3 className={`${isMobile ? "text-sm" : "text-sm"} dark:text-dark-100 font-bold text-gray-900 mb-4 flex items-center gap-2`}>
          <div className="w-1 h-5 bg-[#fe0046] rounded-full"></div>
          Lead Management
        </h3>
        <div className={`grid ${isMobile ? "grid-cols-1 gap-2" : "grid-cols-3 gap-3"}`}>
          {hasPermission("MLLE") && onChangeStage && (
            <Button
              variant="outlined"
              color="primary"
              onClick={onChangeStage}
              disabled={isViewOnly || isChangeStageRestricted}
              className={`justify-center !py-2.5 !px-3 text-[13px] ${(isViewOnly || isChangeStageRestricted) ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <ArrowPathIcon className="mr-2 h-3.5 w-3.5" />
              Change Stage
            </Button>
          )}
          {hasPermission("MLLE") && onSetPriority && (
            <Button
              variant="outlined"
              color="warning"
              onClick={onSetPriority}
              disabled={isViewOnly}
              className={`justify-center !py-2.5 !px-3 text-[13px] ${isViewOnly ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <FlagIcon className="mr-2 h-3.5 w-3.5" />
              Set Priority
            </Button>
          )}
          {hasPermission("MLLE") && onSetInteractionLevel && (
            <Button
              variant="outlined"
              color="neutral"
              onClick={onSetInteractionLevel}
              disabled={isViewOnly}
              className={`justify-center !py-2.5 !px-3 text-[13px] ${isViewOnly ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <FireIcon className="mr-2 h-3.5 w-3.5" />
              Set Interaction
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards Grid - 3 columns */}
      <div
        className={`grid ${isMobile ? "mb-4 grid-cols-1 gap-4" : "mb-0 grid-cols-1 gap-6 md:grid-cols-2"}`}
      >
        {/* Communication Stats Card */}
        <Card
          className={`${isMobile ? "p-4" : "p-5"} transition-all duration-300 hover:shadow-2xl border border-gray-100 dark:border-dark-700/50 bg-white dark:bg-dark-800 shadow-sm relative overflow-hidden group`}
        >
          <div className="mb-4 flex items-center gap-3 relative">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-pink-50 dark:bg-pink-900/40 transition-colors group-hover:bg-pink-100 dark:group-hover:bg-pink-900/60">
              <PaperAirplaneIcon className="h-5 w-5 text-pink-600 dark:text-pink-400" />
            </div>
            <div>
              <h3 className="text-[14px] dark:text-dark-100 font-bold text-gray-900">Communication Stats</h3>
              <p className="text-[11px] text-gray-500 dark:text-dark-400">Interaction overview</p>
            </div>
          </div>

          <div className="space-y-3.5 relative">
            {[
              { label: "Email Sent", value: communicationStats.emailSent, icon: EnvelopeIcon, color: "text-blue-500", bg: "bg-blue-500/10" },
              { label: "SMS Sent", value: communicationStats.smsSent, icon: ChatBubbleLeftRightIcon, color: "text-teal-500", bg: "bg-teal-500/10" },
              { label: "Inbound Call", value: telephonyStats.inboundCalls, icon: PhoneIcon, color: "text-indigo-500", bg: "bg-indigo-500/10" },
              { label: "Outbound Call", value: telephonyStats.outboundCalls, icon: PhoneIcon, color: "text-purple-500", bg: "bg-purple-500/10" }
            ].map((stat, idx) => (
              <div key={idx} className="flex justify-between items-center text-[13px] p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700/30 transition-colors">
                <div className="flex items-center gap-2.5 text-gray-600 dark:text-dark-300">
                  <div className={`p-1.5 rounded-md ${stat.bg}`}>
                    <stat.icon className={`w-3.5 h-3.5 ${stat.color}`} />
                  </div>
                  <span className="font-medium">{stat.label}</span>
                </div>
                <span className="font-bold text-gray-900 dark:text-dark-50 bg-gray-100 dark:bg-dark-700 px-2 py-0.5 rounded text-xs">{stat.value}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Upcoming Follow-ups Card */}
        <Card
          className={`${isMobile ? "p-4" : "p-5"} transition-all duration-300 hover:shadow-2xl border border-gray-100 dark:border-dark-700/50 bg-white dark:bg-dark-800 shadow-sm min-h-[180px] flex flex-col group overflow-hidden`}
        >
          <div className="mb-4 flex items-center justify-between relative">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-900/40 transition-colors group-hover:bg-blue-100 dark:group-hover:bg-blue-900/60">
                <CalendarDaysIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-[14px] dark:text-dark-100 font-bold text-gray-900">Follow-ups</h3>
                <p className="text-[11px] text-gray-500 dark:text-dark-400">Scheduled events</p>
              </div>
            </div>

            {currentFollowUp && hasPermission("FUPSEFUP") && !isViewOnly && (
              <Popover className="relative">
                <PopoverButton className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-500 hover:text-gray-700 dark:text-dark-400 dark:hover:text-dark-200 transition-all">
                  <EllipsisVerticalIcon className="h-5 w-5" />
                </PopoverButton>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <PopoverPanel className="absolute right-0 z-50 mt-1 w-44 rounded-xl bg-white dark:bg-dark-800 shadow-2xl border border-gray-100 dark:border-dark-700 overflow-hidden">
                    <div className="p-1.5 space-y-1">
                      {currentFollowUp && onEditFollowUp && !isStatusFinal(currentFollowUp) && (
                        <button
                          onClick={() => {
                            onEditFollowUp(currentFollowUp);
                          }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-[13px] font-bold flex items-center gap-3 transition-all"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                          Edit Details
                        </button>
                      )}
                      {currentFollowUp && onUpdateStatus && !isStatusFinal(currentFollowUp) && (
                        <button
                          onClick={() => {
                            onUpdateStatus(currentFollowUp);
                          }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/30 text-green-700 dark:text-green-400 text-[13px] font-bold flex items-center gap-3 transition-all"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                          Update Status
                        </button>
                      )}
                    </div>
                  </PopoverPanel>
                </Transition>
              </Popover>
            )}
          </div>

          <div className="flex-1 flex flex-col justify-center items-center py-4 relative">
            {currentFollowUp ? (
              <div className="w-full space-y-3">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-[13px] font-bold text-gray-900 border-l-2 border-primary-500 pl-2">
                      {formatDate(currentFollowUp.ScheduledDate)} at {currentFollowUp.ScheduledDate && currentFollowUp.ScheduledTime
                        ? formatTime(`${currentFollowUp.ScheduledDate}T${currentFollowUp.ScheduledTime}`)
                        : currentFollowUp.scheduledTime || 'Not set'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-dark-400 pl-2 italic line-clamp-2">
                      {currentFollowUp.Remarks || currentFollowUp.remarks || currentFollowUp.Notes || currentFollowUp.notes || currentFollowUp.FollowUpNote || currentFollowUp.NoteDescription || currentFollowUp.FollowUpRemarks || "No remarks"}
                    </p>
                    {(currentFollowUp.AssignedTo || currentFollowUp.assignedToName) && (
                      <p className="text-[11px] text-gray-600 dark:text-dark-300 pl-2 mt-1 flex items-center gap-1.5 font-medium">
                        <UserIcon className="h-3 w-3 text-gray-400" />
                        Assigned to: {currentFollowUp.AssignedTo || currentFollowUp.assignedToName}
                      </p>
                    )}
                  </div>
                  {isFollowUpOverdue(currentFollowUp) && (
                    <Badge variant="soft" color="error" className="text-[10px] font-black uppercase">Overdue</Badge>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center">
                <span className="dark:text-dark-400 text-sm font-medium text-gray-400">
                  No pending follow-ups
                </span>
              </div>
            )}
          </div>

          {/* Create Follow-up Button */}
          {onAddFollowUp && hasPermission("FUPSCFUP") && !isViewOnly && (
            <Button
              onClick={onAddFollowUp}
              variant="outlined"
              color="primary"
              className="w-full justify-center text-xs font-bold mt-auto !py-2 rounded-lg transition-all"
            >
              <PlusIcon className="mr-1.5 h-3.5 w-3.5 stroke-[3px]" />
              Create Follow-up
            </Button>
          )}
        </Card>

        {/* Assigned Personnel Card - Modern Profile Style */}
        <Card
          className={`${isMobile ? "p-4" : "p-5"} transition-all duration-300 hover:shadow-2xl border border-gray-100 dark:border-dark-700/50 bg-white dark:bg-dark-800 shadow-sm relative group overflow-hidden flex flex-col`}
        >
          <div className="mb-4 flex items-center gap-3 relative">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 dark:bg-purple-900/40 transition-colors group-hover:bg-purple-100 dark:group-hover:bg-purple-900/60">
              <UserCircleIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-[14px] dark:text-dark-100 font-bold text-gray-900">Assigned Personnel</h3>
              <p className="text-[11px] text-gray-500 dark:text-dark-400">Team ownership</p>
            </div>
          </div>

          <div className="space-y-3 relative flex-1">
            {[
              { name: leadOwnerName, role: "Lead Owner", assigned: isOwnerAssigned, handler: onAssignOwner, color: "primary" },
            ].map((person, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-50 dark:border-dark-700 last:border-0">
                <div className="flex items-center gap-3">
                  {person.assigned ? (
                    <>
                      <Avatar name={person.name} size={7} initialColor={person.color} />
                      <div>
                        <p className="text-[13px] font-bold text-gray-900 dark:text-dark-50 leading-tight">{person.name}</p>
                        <p className="text-[10px] text-gray-500 font-medium">{person.role}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 dark:bg-dark-700 text-gray-400">
                        <UserCircleIcon className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-[13px] font-bold text-gray-400 dark:text-dark-500 leading-tight italic">Unassigned</p>
                        <p className="text-[10px] text-gray-500 font-medium">{person.role}</p>
                      </div>
                    </>
                  )}
                </div>
                {person.handler && hasPermission("MLASGN") && !isViewOnly && (
                  <button
                    onClick={() => {
                      person.handler();
                    }}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-dark-600 rounded-md text-gray-400 hover:text-primary-600 transition-all flex items-center gap-1.5"
                  >
                    {person.assigned ? (
                      <PencilSquareIcon className="h-4 w-4" />
                    ) : (
                      <span className="text-[10px] font-bold text-primary-600 uppercase tracking-tight">Assign</span>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default StatusCards;
